"use client";

import SectionTitle from "./sectionTitle";
import { Field, FieldError, FieldGroup } from "../ui/field";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { HoverButton } from "./hoverButton";
import useLocalizer from "@/hooks/useLocalizer";
import { createContactSchema } from "@/schema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import zod from "zod";
import { sendMessage } from "@/lib/api";
import { toast } from "sonner";

const ContactForm = () => {
  const { t } = useLocalizer();
  const contactSchema = createContactSchema(t);
  const form = useForm<zod.infer<typeof contactSchema>>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
    },
  });

  const onSubmit = async (data: zod.infer<typeof contactSchema>) => {
    try {
      const response = await sendMessage(data);
      if (response?.statusCode === 200 && response?.data?.isSent) {
        form.reset();
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch {
      toast.error(t("messages.error"));
    }
  };

  return (
    <div className="lg:col-span-2">
      <SectionTitle
        title={t("titles.contactUs")}
        description={t("paragraphs.contactUsDescription")}
      />
      <form id="form-rhf-demo" onSubmit={form.handleSubmit(onSubmit)}>
        <FieldGroup className="my-10">
          <div className="grid lg:grid-cols-2 gap-5">
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input
                    {...field}
                    id="form-rhf-name"
                    aria-invalid={fieldState.invalid}
                    placeholder={t("forms.name") + "*"}
                    autoComplete="off"
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input
                    {...field}
                    id="form-rhf-email"
                    aria-invalid={fieldState.invalid}
                    placeholder={t("forms.email") + "*"}
                    autoComplete="off"
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="phone"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input
                    {...field}
                    id="form-rhf-phone"
                    aria-invalid={fieldState.invalid}
                    placeholder={t("forms.phone") + "*"}
                    autoComplete="off"
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="subject"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Input
                    {...field}
                    id="form-rhf-subject"
                    aria-invalid={fieldState.invalid}
                    placeholder={t("forms.subject") + "*"}
                    autoComplete="off"
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </div>
          <div>
            <Controller
              name="message"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <Textarea
                    {...field}
                    id="form-rhf-message"
                    aria-invalid={fieldState.invalid}
                    placeholder={t("forms.message") + "*"}
                    className="min-h-[150px]"
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </div>
          <div>
            <HoverButton
              disabled={form.formState.isSubmitting}
              type="submit"
              variant="secondary"
              className="text-white min-h-12 min-w-32"
            >
              {form.formState.isSubmitting
                ? t("forms.sending")
                : t("forms.send")}
            </HoverButton>
          </div>
        </FieldGroup>
      </form>
    </div>
  );
};

export default ContactForm;
